import {
	autoUpdate,
	flip,
	offset,
	type Placement,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import React from "react";
import { Portal } from "../portal";
import type { ChildrenProps, ClassNameProps } from "../types";

/** Props Interface */
export interface TooltipProps extends ClassNameProps, ChildrenProps {
	label: string | React.ReactNode;
	openDelay?: number;
	closeDelay?: number;
	isDisabled?: boolean;
	placement?: Placement;
	/** Overrides the z-index utility class of the floating content (default "z-503"). */
	zIndexClassName?: string;
}

export function Tooltip({
	label,
	className,
	children,
	openDelay = 50,
	closeDelay = 0,
	isDisabled,
	placement = "bottom",
	zIndexClassName = "z-503",
}: TooltipProps) {
	/** State */
	const [isOpen, setIsOpen] = React.useState<boolean>(false);

	/** Hooks */
	const { refs, floatingStyles, context } = useFloating({
		placement,
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			offset(5),
			flip(),
			shift({
				padding: 5,
			}),
		],
		whileElementsMounted: autoUpdate,
	});
	const hover = useHover(context, {
		delay: {
			open: openDelay,
			close: closeDelay,
		},
	});
	const click = useClick(context);
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

	if (isDisabled) return children;

	/** Render */
	return (
		<React.Fragment>
			<div
				className={clsx(className)}
				ref={refs.setReference}
				{...getReferenceProps()}
			>
				{children}
			</div>
			{isOpen && (
				<Portal>
					<div
						ref={refs.setFloating}
						{...getFloatingProps()}
						style={floatingStyles}
						className={clsx(
							zIndexClassName,
							"relative shadow-xl rounded-base max-w-[300px] text-left pointer-events-none card",
						)}
					>
						{typeof label === "string" ? (
							<p className="text-color text-xs">{label}</p>
						) : (
							<div className="text-color text-xs">{label}</div>
						)}
					</div>
				</Portal>
			)}
		</React.Fragment>
	);
}
